import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Home, Users, Briefcase, Clock, FileText, Bell, Settings, LogOut, Menu, X,
  ChevronRight, ChevronDown, Plus, CheckCircle, AlertCircle, MapPin, Calendar, User,
  ClipboardList, BarChart3, Play, Square, RefreshCw, Search, Filter, Eye,
  Truck, Camera, Upload, Image, Wrench, Shield, FileImage, Trash2, Save,
  Building2, Sliders, Key, MessageSquare, Send, Car, Fuel, Video, HelpCircle,
  ChevronLeft, Download, Edit3, Check, ArrowLeft, Edit2, UserCheck, Archive, Receipt, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Badge, Button, Card, Input, Modal, Select, Spinner, Textarea, FileUploadInput } from "../../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const SWMSManageView = ({ job, onBack, onRefresh }) => {
  const [swms, setSWMS] = useState(null);
  const [unsignedWorkers, setUnsignedWorkers] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [newSWMS, setNewSWMS] = useState({ name: "", description: "", file_url: "", version: "1.0" });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [job.id]);

  const fetchData = async () => {
    try {
      const [swmsRes, unsignedRes] = await Promise.all([
        axios.get(`${API}/jobs/${job.id}/swms`),
        axios.get(`${API}/jobs/${job.id}/unsigned-workers`)
      ]);
      setSWMS(swmsRes.data);
      setUnsignedWorkers(unsignedRes.data.unsigned_workers || []);
      
      if (swmsRes.data?.id) {
        const sigRes = await axios.get(`${API}/jobs/${job.id}/swms/${swmsRes.data.id}/signatures`);
        setSignatures(sigRes.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!newSWMS.file_url) {
      alert("Please provide the SWMS document URL");
      return;
    }
    setUploading(true);
    try {
      await axios.post(`${API}/jobs/${job.id}/swms`, newSWMS);
      alert("SWMS uploaded! All assigned workers will be notified.");
      setShowUpload(false);
      setNewSWMS({ name: "", description: "", file_url: "", version: "1.0" });
      fetchData();
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to upload SWMS");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this SWMS? All signatures will be removed.")) return;
    try {
      await axios.delete(`${API}/jobs/${job.id}/swms/${swms.id}`);
      alert("SWMS deleted");
      fetchData();
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete SWMS");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold uppercase flex items-center gap-2">
          <Shield className="w-5 h-5" />
          SWMS Management
        </h3>
        <Button variant="ghost" size="sm" onClick={onBack}>Back</Button>
      </div>

      {swms ? (
        <>
          <Card className="border-green-200 bg-green-50">
            <div className="flex items-start justify-between">
              <div>
                <Badge variant="success">Active SWMS</Badge>
                <h4 className="font-bold mt-2">{swms.name}</h4>
                {swms.description && <p className="text-sm text-slate-600">{swms.description}</p>}
                <p className="text-xs text-slate-500 mt-2">Version: {swms.version} • Uploaded {new Date(swms.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => window.open(swms.file_url, '_blank')}>
                  <Eye className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={handleDelete}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Signature Status */}
          <Card>
            <h4 className="font-bold mb-4">Signature Status</h4>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{signatures.length}</p>
                <p className="text-sm text-green-700">Signed</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-red-600">{unsignedWorkers.length}</p>
                <p className="text-sm text-red-700">Not Signed</p>
              </div>
            </div>

            {unsignedWorkers.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-red-600 mb-2">Workers who haven't signed:</p>
                <div className="space-y-1">
                  {unsignedWorkers.map(w => (
                    <div key={w.id} className="text-sm p-2 bg-red-50 rounded flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      {w.first_name} {w.last_name} ({w.role?.replace('_', ' ')})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {signatures.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-green-600 mb-2">Signed by:</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {signatures.map(s => (
                    <div key={s.id} className="text-sm p-2 bg-green-50 rounded flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {s.user_name}
                      </span>
                      <span className="text-xs text-slate-500">{new Date(s.signed_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          <Button variant="outline" onClick={() => setShowUpload(true)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Upload New Version (requires re-signing)
          </Button>
        </>
      ) : (
        <div className="text-center py-8">
          <Shield className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-semibold text-slate-600">No SWMS assigned to this job</p>
          <p className="text-sm text-slate-500 mb-4">Upload a SWMS document for workers to sign before they can clock in</p>
          <Button onClick={() => setShowUpload(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Upload SWMS
          </Button>
        </div>
      )}

      {/* Upload Form */}
      {showUpload && (
        <Card className="border-orange-200">
          <form onSubmit={handleUpload} className="space-y-4">
            <h4 className="font-bold">Upload SWMS Document</h4>
            
            <Input
              label="SWMS Name"
              value={newSWMS.name}
              onChange={(e) => setNewSWMS({...newSWMS, name: e.target.value})}
              placeholder="e.g., Tower Crane Installation SWMS"
              required
            />
            
            <Textarea
              label="Description (optional)"
              value={newSWMS.description}
              onChange={(e) => setNewSWMS({...newSWMS, description: e.target.value})}
              placeholder="Key hazards and requirements..."
            />
            
            <FileUploadInput
              label="Upload SWMS Document"
              accept=".pdf,.doc,.docx"
              onFileSelect={(fileUrl, fileName) => setNewSWMS({...newSWMS, file_url: fileUrl})}
              currentFile={newSWMS.file_url ? "Document uploaded" : null}
              onClear={() => setNewSWMS({...newSWMS, file_url: ""})}
            />
            
            <Input
              label="Version"
              value={newSWMS.version}
              onChange={(e) => setNewSWMS({...newSWMS, version: e.target.value})}
              placeholder="1.0"
            />
            
            <div className="flex gap-2">
              <Button type="submit" disabled={uploading || !newSWMS.file_url}>
                {uploading ? <Spinner size="sm" /> : "Upload SWMS"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowUpload(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
};

// Daily Roster Component for Job

export default SWMSManageView;
